import { useEffect, useRef } from 'react'
import { NavigateFunction, NavigateOptions, To, useNavigate } from 'react-router-dom'

/**
 * This hook functions essentially like useNavigate from react router,
 *  with the slight benefit that it always uses the most current reference of the navigate function.
 * There is nothing wrong with useNavigate, but in cases where the navigation should happen based on
 *  conditions that have mutated the state (where the navigate function lives), e.g. an api-call,
 *  this version will not lose the hook context that appropriately helps react router re-render the new route.
 */
export const useReferencedNavigate = (): NavigateFunction => {
    const navigate = useNavigate()
    const ref = useRef(navigate)

    useEffect(() => {
        ref.current = navigate
    }, [navigate])

    // NOTE: Overloaded navigate function makes generalizing in TypeScript quite inconvenient.
    // This is because TS cannot generalize overloads by types alone, but also requires an implementation for each overload.
    // I.e. even though it is perfectly fine and functional javascript, this will not work for typescript:
    //  (...args: Parameters<NavigateFunction>) => ref.current(...args)
    return (toOrDelta: To | number, options?: NavigateOptions) =>
        typeof toOrDelta === 'number'
            ? ref.current(toOrDelta as number)
            : ref.current(toOrDelta as To, options)
}
